import type { CompanyLookup } from '@/shared/api/resources';
import { avisoDaReceita } from '@/shared/lib/consultaCnpj';
import { formatDate } from '@/shared/lib/format';

import { Alert } from './Alert';
import { Button } from './Button';
import { Spinner } from './Spinner';
import type { ConsultaCnpj } from './useConsultaCnpj';

/** O que a Receita diz da empresa e nao vira campo do formulario. */
function resumoDaEmpresa(r: CompanyLookup): string {
  const partes: string[] = [];
  if (r.registration_situation_text) {
    partes.push(
      `Situacao cadastral ${r.registration_situation_text}` +
        (r.registration_situation_date
          ? ` desde ${formatDate(r.registration_situation_date)}`
          : ''),
    );
  }
  if (r.head_office) partes.push(r.head_office === 'matriz' ? 'Matriz' : 'Filial');
  if (r.company_size_text) partes.push(`Porte na Receita: ${r.company_size_text}`);
  if (r.simples_option !== null)
    partes.push(`Simples: ${r.simples_option ? 'sim' : 'nao'}`);
  if (r.mei_option !== null) partes.push(`MEI: ${r.mei_option ? 'sim' : 'nao'}`);
  return partes.join(' · ');
}

/**
 * Aviso da consulta de CNPJ no formulario (ADR 0092): consultando, a falha (que nao
 * trava nada), ou o que foi preenchido, com a origem e a data, e a pergunta antes de
 * trocar o que a pessoa ja tinha digitado.
 */
export function AvisoDaConsultaCnpj<C extends string>({
  consulta,
}: {
  consulta: ConsultaCnpj<C>;
}) {
  const s = consulta.situacao;
  if (s.tipo === 'ocioso') return null;

  if (s.tipo === 'consultando') {
    return (
      <p role="status" className="flex items-center gap-2 text-2xs text-ink-600">
        <Spinner size={14} />
        Consultando o CNPJ na Receita Federal pela SeguraCoreData.
      </p>
    );
  }

  if (s.tipo === 'erro') {
    return (
      <Alert
        tone={s.desligada ? 'info' : 'warning'}
        title={
          s.desligada ? 'Consulta de CNPJ desligada' : 'Consulta do CNPJ sem resultado'
        }
      >
        {s.mensagem} Os campos continuam livres para digitar.
      </Alert>
    );
  }

  const r = s.resultado;
  const resumo = resumoDaEmpresa(r);
  return (
    <Alert tone="info" title={avisoDaReceita(r.consulted_at)}>
      <div className="flex flex-col gap-2">
        {resumo ? <p>{resumo}.</p> : null}
        {r.special_situation ? (
          <p className="font-semibold text-warning-700">
            Situacao especial na Receita: {r.special_situation}
            {r.special_situation_date
              ? ` desde ${formatDate(r.special_situation_date)}`
              : ''}
            .
          </p>
        ) : null}
        <p>
          {s.preenchidos.length > 0
            ? `Preenchidos: ${s.preenchidos.map((campo) => campo.rotulo).join(', ')}. Confira antes de salvar.`
            : 'Nenhum campo vazio para preencher.'}{' '}
          So campo vazio e preenchido; o que voce digitou fica como esta.
        </p>
        {s.divergentes.length > 0 ? (
          <div className="flex flex-col gap-2">
            <p className="font-semibold">
              Estes campos ja tinham valor e foram mantidos. A Receita traz:
            </p>
            <ul
              className="flex flex-col gap-1"
              aria-label="Campos com valor diferente na Receita"
            >
              {s.divergentes.map((campo) => (
                <li key={campo.chave}>
                  <span className="font-semibold">{campo.rotulo}:</span> {campo.receita}{' '}
                  <span className="text-ink-500">(no formulario: {campo.atual})</span>
                </li>
              ))}
            </ul>
            <div className="flex flex-wrap gap-2">
              <Button size="sm" variant="secondary" onClick={consulta.substituir}>
                Usar os dados da Receita nesses campos
              </Button>
              <Button size="sm" variant="ghost" onClick={consulta.manter}>
                Manter o que esta no formulario
              </Button>
            </div>
          </div>
        ) : null}
        {r.notes.length > 0 ? (
          <ul
            className="list-disc pl-4 text-2xs"
            aria-label="O que a consulta nao preencheu"
          >
            {r.notes.map((nota) => (
              <li key={nota}>{nota}</li>
            ))}
          </ul>
        ) : null}
      </div>
    </Alert>
  );
}
